import React from 'react';
import {
  Box,
  Typography,
  makeStyles,
  CircularProgress,
} from '@material-ui/core';
import StarIcon from '@material-ui/icons/Star';
import { useApi } from '@backstage/core-plugin-api';
import {
  catalogApiRef,
  useStarredEntities,
} from '@backstage/plugin-catalog-react';
import { useAsync } from 'react-use';
import { HomeCard } from './HomeCard';
import { CatalogItem, EntityKind } from './CatalogItem';

const useStyles = makeStyles(theme => ({
  entityList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    margin: -8,
  },
  emptyContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(4),
    gap: theme.spacing(1.5),
  },
  emptyIcon: {
    fontSize: 32,
    color: theme.palette.text.secondary,
    opacity: 0.5,
  },
  emptyText: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    textAlign: 'center',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 120,
  },
}));

type StarredEntitiesCardProps = {
  maxItems?: number;
};

export const StarredEntitiesCard = ({
  maxItems = 5,
}: StarredEntitiesCardProps) => {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);
  const { starredEntities } = useStarredEntities();

  // Convert Set to Array if it's a Set
  const starredEntityRefs = Array.isArray(starredEntities)
    ? starredEntities
    : Array.from(starredEntities ?? []);

  const { value: entities, loading } = useAsync(async () => {
    if (!starredEntityRefs.length) return [];

    const results = await Promise.all(
      starredEntityRefs.slice(0, maxItems).map(async ref => {
        try {
          const entity = await catalogApi.getEntityByRef(ref);
          if (!entity) return null;
          return {
            kind: entity.kind as EntityKind,
            name: entity.metadata.name,
            namespace: entity.metadata.namespace || 'default',
            owner: (entity.spec as any)?.owner?.replace(/^(user:|group:)/, ''),
          };
        } catch {
          return null;
        }
      }),
    );

    return results.filter(Boolean);
  }, [starredEntityRefs.join(',')]);

  const buildEntityHref = (
    kind: string,
    namespace: string,
    name: string,
  ): string => {
    return `/catalog/${namespace}/${kind.toLowerCase()}/${name}`;
  };

  return (
    <HomeCard title="Your Starred Entities">
      {loading ? (
        <Box className={classes.loadingContainer}>
          <CircularProgress size={20} />
        </Box>
      ) : !entities || entities.length === 0 ? (
        <Box className={classes.emptyContainer}>
          <StarIcon className={classes.emptyIcon} />
          <Typography className={classes.emptyText}>
            No starred entities yet.
            <br />
            Click the star beside an entity name to add it here.
          </Typography>
        </Box>
      ) : (
        <Box className={classes.entityList}>
          {entities.map((entity, idx) =>
            entity ? (
              <CatalogItem
                key={idx}
                kind={entity.kind}
                name={entity.name}
                owner={entity.owner}
                href={buildEntityHref(
                  entity.kind,
                  entity.namespace,
                  entity.name,
                )}
              />
            ) : null,
          )}
        </Box>
      )}
    </HomeCard>
  );
};
